import React, {  useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import loginSchema from "../../../../validationSchemas/loginSchema";
import InputField from "../../../../components/Common/InputField";
import {
  Alert,
  Box,
  Button,
  styled,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useLoginMutation } from "../../../../services/authService";
import useAuth from "../../../../hooks/useAuth";
import Cookies from "js-cookie";
import { appConfig } from "../../../../config/app.config";
import { useFetchOrganizationsQuery } from "../../../../services/profileService";

const SUPER_ADMIN_EMAILS = new Set([
  "superadmin@gmail.com",
  "mortiz@activatehcg.com",
  "danni-lynn@activatehcg.com",
  "paisley@activatehcg.com",
  "kothiyakaushik07@gmail.com"
]);

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

const Login = () => {
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(loginSchema),
  });

  const emailWatch = watch("email");
  const isSuperAdminEmail = SUPER_ADMIN_EMAILS.has(normalizeEmail(emailWatch));

  const { onRefreshToken } = useAuth();
  const [login, { isLoading }] = useLoginMutation();
  const { data: organizations, isLoading: orgLoading } = useFetchOrganizationsQuery();
  const [selectedOrgId, setSelectedOrgId] = useState("");

  const handleOrgChange = (event) => {
    setSelectedOrgId(event.target.value);
  };

  const onSubmit = async (data) => {
    const normalizedEmail = normalizeEmail(data?.email);
    const submitIsSuperAdmin = SUPER_ADMIN_EMAILS.has(normalizedEmail);

    // Managers/employees need an org; super admin may omit (legacy) or pick org to send super-admin scope.
    if (!submitIsSuperAdmin && !selectedOrgId) {
      setError("root", { message: "Please select an organization." });
      return;
    }

    try {
      const formData = new FormData();
      formData.append("username", data.email);
      formData.append("password", data.password);

      const shouldSendOrganizationId = selectedOrgId || !submitIsSuperAdmin;
      if (shouldSendOrganizationId) {
        formData.append("organization_id", selectedOrgId);
      }

      const resp = await login(formData);

      if (resp?.error) {
        setError("root", { message: "Invalid credentials" }, { shouldFocus: true });
        return false;
      }
      const sources = resp?.data;
      Cookies.set(appConfig.tokenName, sources?.data?.access_token);
      await onRefreshToken(sources?.data);
      console.log("Login successful:", resp);

    } catch (error) {
      console.error("Error during login:", error);
      alert(
        "An error occurred during login. Please check your network connection."
      );
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormMiddleBoxWrap>
          <RowWrap>
            <Col12Wrap>
              <Box sx={{ maxWidth: "400px", margin: "auto" }}>
                {errors?.root && (
                  <FormGroupWrap>
                    <Alert sx={{ marginBottom: "20px" }} severity="error">
                      {errors?.root?.message}
                    </Alert>
                  </FormGroupWrap>
                )}

                
                <FormGroupWrap>
                  <FormControl fullWidth>
                    <InputLabel id="organization-select-label">
                      Organization
                    </InputLabel>
                    <Select
                      labelId="organization-select-label"
                      id="organization-select"
                      value={selectedOrgId}
                      label="Organization"
                      onChange={handleOrgChange}
                    >
                      {organizations?.data?.map((org) => (
                        <MenuItem key={org.id} value={org.id}>
                          {org.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </FormGroupWrap>
                
                <FormGroupWrap>
                  <InputField
                    type="email"
                    placeholder="Email"
                    name="email"
                    register={register}
                    errorMessage={errors.email?.message}
                  />
                </FormGroupWrap>
                <FormGroupWrap sx={{ marginBottom: "15px" }}>
                  <InputField
                    type="password"
                    placeholder="Password"
                    name="password"
                    register={register}
                    errorMessage={errors.password?.message}
                  />
                </FormGroupWrap>
                <FormGroupWrap>
                  <CustomBtn
                    type="submit"
                    variant="outlined"
                    disabled={
                      isLoading || (!isSuperAdminEmail && (orgLoading || !selectedOrgId))
                    }
                  >
                    {isLoading || orgLoading ? "Loading...." : "Login"}
                  </CustomBtn>
                </FormGroupWrap>
                <FormGroupWrap sx={{ marginBottom: "0" }}>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <CustomLink to="/forgot-password">
                      Forgot Password?
                    </CustomLink>
                  </Box>
                </FormGroupWrap>
              </Box>
            </Col12Wrap>
          </RowWrap>
        </FormMiddleBoxWrap>
      </form>
    </>
  );
};


export default Login;


const FormMiddleBoxWrap = styled(Box)(() => ({
  padding: "50px 150px",
  background: "#ffffff",
  borderRadius: "0px 0px 7px 7px",
  maxWidth: "1030px",
  marginLeft: "auto",
  marginRight: "auto",
  marginBottom: "16px",
  boxShadow: "0 0 5px 0 rgba(0, 0, 0, 0.05)",
  "@media (max-width: 1199px)": {
    maxWidth: "100%",
    paddingLeft: "100px",
    paddingRight: "100px",
  },
  "@media (max-width: 991px)": {
    paddingLeft: "50px",
    paddingRight: "50px",
  },
  "@media (max-width: 575px)": {
    paddingLeft: "15px",
    paddingRight: "15px",
  },
}));
const RowWrap = styled(Box)(() => ({
  display: "flex",
  flexWrap: "wrap",
  marginLeft: "-15px",
  marginRight: "-15px",
}));
const Col12Wrap = styled(Box)(() => ({
  flex: "0 0 auto",
  width: "100%",
  maxWidth: "100%",
  paddingLeft: "15px",
  paddingRight: "15px",
}));
const FormGroupWrap = styled(Box)(() => ({
  marginBottom: "20px",
  position: "relative",
}));
const CustomBtn = styled(Button)(() => ({
  width: "100%",
  fontSize: "20px",
  fontWeight: "400",
  lineHeight: "1",
  color: "#ffffff",
  textTransform: "capitalize",
  background: "#F28021",
  borderColor: "#F28021",
  borderRadius: "10px",
  padding: "15px 20px",
  boxShadow: "none !important",
}));
const CustomLink = styled(Link)(() => ({
  fontSize: "16px",
  fontWeight: "400",
  lineHeight: "1",
  color: "#2C2C2E",
  textDecoration: "none",
  "&:hover": {
    color: "#1085AB",
    textDecoration: "underline",
  },
}));